import { MailOutlined } from "@ant-design/icons";
import { Button, Divider, Form, Input, Space, Typography, message } from "antd";
import { useState } from "react";
import EhrClinicianList, { type EhrClinicianRow } from "../../components/EhrClinicianList/EhrClinicianList.component";
import {
  type SetupPortal,
  useCompleteGoLiveMutation,
  useInviteProviderMutation,
} from "../../service/onboarding.service";
import styles from "./setup.module.scss";
import { apiErrorMessage } from "./setup.shared";

const { Text } = Typography;

/** Step 6 — invite clinicians and go live. */
export default function GoLiveStep({ token, portal }: { token: string; portal: SetupPortal }) {
  const goLive = portal.go_live;
  const [invite] = useInviteProviderMutation();
  const [complete, { isLoading: completing }] = useCompleteGoLiveMutation();
  const [sendingFreeForm, setSendingFreeForm] = useState(false);

  const isComplete = portal.steps.find((s) => s.key === "go_live")?.status === "complete";

  const inviteFromList = async (email: string, row: EhrClinicianRow) => {
    try {
      const res = await invite({
        token,
        email,
        name: row.name,
        ehr_provider_id: row.ehr_provider_id,
      }).unwrap();
      const state = res.invite_result?.state ?? "invited";
      message.success(state === "joined" ? `${email} was added to your group.` : `Invitation sent to ${email}.`);
      return state;
    } catch (err) {
      throw new Error(apiErrorMessage(err, "We couldn't send that invitation. Please try again."));
    }
  };

  const doInvite = async (email: string) => {
    const clean = email.trim();
    if (!clean) {
      message.warning("Enter an email address to invite them.");
      return;
    }
    setSendingFreeForm(true);
    try {
      const res = await invite({ token, email: clean, name: "", ehr_provider_id: "" }).unwrap();
      const state = res.invite_result?.state ?? "invited";
      message.success(state === "joined" ? `${clean} was added to your group.` : `Invitation sent to ${clean}.`);
    } catch (err) {
      message.error(apiErrorMessage(err, "We couldn't send that invitation. Please try again."));
    } finally {
      setSendingFreeForm(false);
    }
  };

  return (
    <>
      {goLive.has_directory && goLive.providers.length > 0 ? (
        <>
          <EhrClinicianList
            providers={goLive.providers}
            onInvite={inviteFromList}
            hint="Pulled from your EHR. Invite everyone who should write notes in Nudge — each one gets a link to set up their own login."
          />
          <Divider style={{ margin: "18px 0 14px" }} />
        </>
      ) : (
        <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 14 }}>
          Invite everyone who should write notes in Nudge — each one gets a link to set up their own login. If we can
          read your clinician list from your EHR, it'll appear here too.
        </Text>
      )}

      <p className={styles.sectionLabel}>Invite someone by email</p>
      <Form
        layout="inline"
        onFinish={(v: { email: string }) => doInvite(v.email)}
        style={{ marginBottom: 8, rowGap: 8 }}
      >
        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Enter an email address." },
            { type: "email", message: "That doesn't look like an email address." },
          ]}
          style={{ flex: 1, minWidth: 220 }}
        >
          <Input prefix={<MailOutlined />} placeholder="colleague@yourpractice.com" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={sendingFreeForm}>
            Send invite
          </Button>
        </Form.Item>
      </Form>

      <Divider style={{ margin: "14px 0" }} />

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {!isComplete ? (
          <>
            {!goLive.can_complete ? (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Keep inviting — we're still setting up your templates and your AI Medical Assistant. You'll be able to
                go live here as soon as that's done, and we'll email you when it is.
              </Text>
            ) : null}
            <Button
              type="primary"
              disabled={!goLive.can_complete}
              loading={completing}
              onClick={async () => {
                try {
                  await complete({ token }).unwrap();
                  message.success("You're all set — welcome aboard!");
                } catch (err) {
                  message.error(apiErrorMessage(err, "We couldn't update that. Please try again."));
                }
              }}
            >
              We've invited everyone — we're live
            </Button>
          </>
        ) : null}
      </Space>
    </>
  );
}
